/**
 * The drawer tree's files: each asset source (the project's files, this computer's) with its
 * folders. Click to browse; right-click for New folder / Rename / Delete; drop an asset card on
 * a folder to move (same source) or copy (another source) it there.
 */
import { For, Show, createSignal, type JSX } from "solid-js";
import type { AssetSource } from "@protocol/AssetSource";
import { useApp } from "../../app/context";
import type { AssetSourceView } from "../../state/assets";
import { assetName, childFolders, countBelow } from "../../shared/assets";
import type { FileTools } from "./fileTools";

/** Every source of files with its folders. */
export function FileTree(props: { tools: FileTools }): JSX.Element {
  const { assets } = useApp();
  return <For each={assets.sources()}>{(source) => <FileSourceRows source={source} tools={props.tools} />}</For>;
}

/** One source: its row, then its folders. */
function FileSourceRows(props: { source: AssetSourceView; tools: FileTools }): JSX.Element {
  const { assets, menu } = useApp();
  const here = () => assets.place()?.source === props.source.id && assets.place()?.folder === "";
  return (
    <div class="ptree-source" data-asset-source={props.source.id}>
      <button
        type="button"
        class="ptree-row ptree-top"
        aria-current={here() ? "true" : undefined}
        title={props.source.readOnly ? props.source.why : undefined}
        data-tree-asset-source={props.source.id}
        data-asset-drop-source={props.source.readOnly ? undefined : props.source.id}
        data-asset-drop-folder=""
        onClick={() => assets.go({ source: props.source.id, folder: "" })}
        onContextMenu={(e) => menu.open(e, props.tools.folderMenu(props.source.id, ""))}
      >
        <span class="ptree-name">{props.source.label}</span>
        <span class="ptree-count">{props.source.listing.files.length}</span>
      </button>
      <FolderRows source={props.source.id} readOnly={props.source.readOnly} parent="" depth={1} tools={props.tools} />
    </div>
  );
}

/** The folders inside `parent`, each followed by its own. */
function FolderRows(props: { source: AssetSource; readOnly: boolean; parent: string; depth: number; tools: FileTools }): JSX.Element {
  const { assets, menu } = useApp();
  const [closed, setClosed] = createSignal<ReadonlySet<string>>(new Set());
  const listing = () => assets.source(props.source)?.listing ?? { source: props.source, folders: [], files: [] };
  return (
    <For each={childFolders(listing(), props.parent)}>
      {(path) => {
        const open = () => !closed().has(path);
        return (
          <>
            <button
              type="button"
              class="ptree-row"
              style={{ "padding-left": `${8 + props.depth * 14}px` }}
              aria-current={assets.place()?.source === props.source && assets.place()?.folder === path ? "true" : undefined}
              data-tree-asset-source={props.source}
              data-tree-asset-folder={path}
              data-asset-drop-source={props.readOnly ? undefined : props.source}
              data-asset-drop-folder={path}
              onClick={() => assets.go({ source: props.source, folder: path })}
              onContextMenu={(e) => menu.open(e, props.tools.folderMenu(props.source, path))}
            >
              <span
                class="ptree-arrow"
                classList={{ open: open(), none: !childFolders(listing(), path).length }}
                onClick={(e) => {
                  e.stopPropagation();
                  const next = new Set(closed());
                  if (open()) next.add(path);
                  else next.delete(path);
                  setClosed(next);
                }}
              >
                ▸
              </span>
              <span class="ptree-name">{assetName(path)}</span>
              <span class="ptree-count">{countBelow(listing(), path)}</span>
            </button>
            <Show when={open()}>
              <FolderRows source={props.source} readOnly={props.readOnly} parent={path} depth={props.depth + 1} tools={props.tools} />
            </Show>
          </>
        );
      }}
    </For>
  );
}
